/**
 * 曜日配列
 */
export const WEEK_LIST = [
  {id: 'Monday', key: 'mon', name: '月' },
  {id: 'Tuesday', key: 'tue', name: '火' },
  {id: 'Wednesday', key: 'wed', name: '水' },
  {id: 'Thursday', key: 'thu', name: '木' },
  {id: 'Friday', key: 'fri', name: '金' },
  {id: 'Saturday', key: 'sat', name: '土' },
  {id: 'Sunday', key: 'sun', name: '日' }
]

/**
 * 雇い入れ➡AKBS（req_akbs.status）の状況
 * ・未保存：面接・雇い入れのメインタスクを登録しただけで、入力画面はまだ誰も保存していない（空の枠。清掃員の申請だけ）
 * ・一時保存：入力途中（申請前）。必須項目が未入力でも保存できる
 * ・承認待ち：申請済み。承認者が内容を確認してAKBSへ登録するのを待っている（内容は閲覧のみ）
 * ・差戻：承認者が内容を直してほしいと申請者へ戻した。直してもう一度申請する
 * ・AKBS登録済み：承認され、本番のAKBSへ書き込み済み（内容は閲覧のみ）
 * ・削除：取り下げ
 */
export const AKBS_STATUS = {
  UNSAVED: '未保存',
  SAVED: '一時保存',
  PENDING: '承認待ち',
  RETURNED: '差戻',
  REGISTERED: 'AKBS登録済み',
  DELETED: '削除',
}

/**
 * AKBS の申請画面への入口になるサブタスクの名前（サブタスクマスタの名前と合わせる。スマイル移行後の名前）
 * サブタスクの担当者がやるのは「入力して申請する」までで、本番の AKBS に入るのは承認されたときなので、名前は「登録申請」
 * どちらも、サブタスク名にこの文字を含むかで見る（マスタの名前の前後に文字が付いても当たるように）
 * ・契約詳細登録申請：契約詳細入力画面（AkbsContractForm）へ。受注処理・変更処理の黄色い帯と、サブタスク画面のボタンを、このサブタスクがあるときだけ出す
 *   （スマイル移行前の受注の「スマイル登録」の代わり。受注のサブタスクを作るときに自動で足す）
 * ・契約詳細承認：契約詳細登録申請とセット（申請したものを承認者が承認して AKBS に登録する）。受注のサブタスクでは、片方だけ欠けていたらそれも足す
 * ・雇用指示登録申請：清掃員の申請画面（AkbsInputForm）へ。サブタスク画面のボタン
 *   （スマイル移行前の雇い入れの「スマイル登録」）
 * ・雇用指示承認：雇用指示登録申請とセット（スマイル移行前の「スマイル入力確認」）。雇い入れ（清掃員）のサブタスクでは、片方だけ欠けていたらそれも足す
 */
export const AKBS_SUB_TASK = {
  CONTRACT: '契約詳細登録申請',
  CONTRACT_APPROVE: '契約詳細承認',
  EMPLOYMENT: '雇用指示登録申請',
  EMPLOYMENT_APPROVE: '雇用指示承認'
}

/**
 * サブタスク名が、その申請画面への入口のサブタスクか
 * @param {String} requestTask サブタスク名
 * @param {String} name AKBS_SUB_TASK のどれか
 */
export function isAkbsSubTask(requestTask, name) {
  return String(requestTask || '').includes(name)
}

/**
 * 申請画面の「サブタスクの部署と自分の部署が違う」の確認で、同じ部署とみなす組（部署 ID と部署名）
 * 建物管理部と勤怠採用課は同じ扱い
 */
const SAME_DEPARTMENT_GROUPS = [
  { ids: ['d1442', 'd1447'], names: ['建物管理部', '勤怠採用課'] }
]

/**
 * サブタスクの部署とログインユーザーの主な部署が同じか（申請画面の一時保存・申請・AKBSへ登録の前の確認に使う）
 * ID がそろっていれば ID で、無ければ部署名で比べる。SAME_DEPARTMENT_GROUPS の組は同じとみなす
 * @param {Object} task サブタスク（departmentId・departmentName）
 * @param {Object} me ログインユーザー（departmentIdUser・departmentName）
 */
export function isSameDepartmentForAkbs(task, me) {
  const taskId = String(task.departmentId || '')
  const meId = String(me.departmentIdUser || '')
  if (taskId && meId) {
    return taskId === meId || SAME_DEPARTMENT_GROUPS.some(g => g.ids.includes(taskId) && g.ids.includes(meId))
  }
  const taskName = String(task.departmentName || '')
  const meName = String(me.departmentName || '')
  return taskName === meName || SAME_DEPARTMENT_GROUPS.some(g => g.names.includes(taskName) && g.names.includes(meName))
}

/**
 * 申請の状況のバッジの色（Badge 部品の color。見積リストの同じ意味の状態と色を揃えている）
 * 一時保存＝作成中、承認待ち＝承認待ち、差戻＝差戻、AKBS登録済み＝承認済み、削除＝却下
 */
export const AKBS_STATUS_COLOR = {
  [AKBS_STATUS.UNSAVED]: 'gray',
  [AKBS_STATUS.SAVED]: 'orange',
  [AKBS_STATUS.PENDING]: 'pink',
  [AKBS_STATUS.RETURNED]: 'red',
  [AKBS_STATUS.REGISTERED]: 'lightGreen',
  [AKBS_STATUS.DELETED]: 'black',
}

/**
 * AKBS入力画面の申請の種別（req_akbs.type の数値 → 表示名）
 * 入力画面がメインタスクの種別・変更区分から決め、承認待ち一覧の表示と絞り込みにも使う
 */
export const AKBS_TYPE_NAMES = {
  1: '新規入社',
  2: '物件追加',
  3: '契約内容変更',
  4: '金額のみ変更',
  5: '作業終了',
  6: '退職',
  7: '再入社',
  100: 'その他',
}

/**
 * 勤務形態区分
 */
export const ATTENDANCE = [
  {id: '00', name: '毎週'},
  {id: '01', name: '第1'},
  {id: '02', name: '第2'},
  {id: '03', name: '第3'},
  {id: '04', name: '第4'},
  {id: '05', name: '第5'},
  {id: '10', name: '隔週'}
]

/**
 * 面接・雇入れ共通（作業内容）
 */
export const WORK_SELECTIONS = [
  {
    name: '日常清掃',
    id: '10'
  },
  {
    name: '管理員',
    id: '20'
  },
  {
    name: '管理員（日常清掃含）',
    id: '30'
  },
  {
    name: '巡回員',
    id: '40'
  },
  {
    name: '代勤専門',
    id: '50'
  }
]

/**
 * 祝日区分
 */
export const HOLIDAYTYPE_SELECTIONS = [
  {
    id: 100,
    name: 'FULL'
  },
  {
    id: 1,
    name: '1時間'
  },
  {
    id: 2,
    name: '2時間'
  },
  {
    id: 3,
    name: '3時間'
  },
  {
    id: 0,
    name: '無'
  }
]

/**
 * 有り無しCODE
 */
export const YES_NO_CODE = [
  {
    id: 1,
    name: '有'
  },
  {
    id: 0,
    name: '無'
  }
]

/**
 * 交通費区分
 */
export const PROVISION_SELECTIONS = [
  {
    id: 1,
    name: '1日'
  },
  {
    id: 2,
    name: '1ヶ月'
  },
  {
    id: 0,
    name: '無'
  }
]

/**
 * 面接・雇入れ共通（東京23区+α）
 */
export const AREA_SELECTIONS = [
  '神奈川エリア',
  '千葉エリア',
  '埼玉エリア',

  '千代田区エリア',
  '中央区エリア',
  '港区エリア',
  '新宿区エリア',
  '文京区エリア',
  '台東区エリア',
  '墨田区エリア',
  '江東区エリア',
  '品川区エリア',
  '目黒区エリア',
  '大田区エリア',
  '世田谷区エリア',
  '渋谷区エリア',
  '中野区エリア',
  '杉並区エリア',
  '豊島区エリア',
  '北区エリア',
  '荒川区エリア',
  '板橋区エリア',
  '練馬区エリア',
  '足立区エリア',
  '葛飾区エリア',
  '江戸川区エリア',

  // 多摩は全体の「多摩エリア」と、北・南に分けたもの（AKBS の雇用指示の営業所名と同じ）
  '多摩エリア',
  '多摩北エリア',
  '多摩南エリア'
]

/**
 * 営業所（雇用指示ごとに持つ。最低賃金はこの営業所のものと比べる。AKBS の mst_instruct_employment.sales_office・mst_min_wage.sales_office と同じ値）
 */
export const SALES_OFFICE_SELECTIONS = ['東京', '千葉']

/**
 * 画面での呼び方と説明（ツールチップ）。「営業所」だと雇用指示営業所・担当営業所と紛らわしいので「最低賃金判定」と出す
 */
export const SALES_OFFICE_CAPTION = '最低賃金判定'
export const SALES_OFFICE_TIP = 'どちらの最低賃金と比べるか。東京＝東京都・神奈川県の物件、千葉＝埼玉県・千葉県の物件'

/**
 * 最低賃金マスタと賃金一括変更の区分（東京／千葉／埼玉）
 * 雇用指示の最低賃金判定（東京／千葉）とは別。埼玉は一括変更で「最低賃金判定が千葉で、物件住所が埼玉県」の雇用指示に使う
 * 入力画面・代勤・欠員募集の最低賃金チェックは東京／千葉の行だけを見る（埼玉の行は使わない）
 */
export const MIN_WAGE_OFFICE_SELECTIONS = ['東京', '千葉', '埼玉']
export const MIN_WAGE_OFFICE_NOTE = {
  '東京': '最低賃金判定が「東京」の雇用指示',
  '千葉': '最低賃金判定が「千葉」で、物件住所が埼玉県以外の雇用指示',
  '埼玉': '最低賃金判定が「千葉」で、物件住所が埼玉県の雇用指示（代勤専門は清掃員の住所）'
}

/**
 * 都道府県 → 営業所（東京＝東京都・神奈川県、千葉＝埼玉県・千葉県。それ以外の県は決まらない）
 */
export const SALES_OFFICE_BY_PREF = {
  '東京都': '東京',
  '神奈川県': '東京',
  '埼玉県': '千葉',
  '千葉県': '千葉'
}